import React, { KeyboardEvent, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StyleSettings } from "../../styles/StyleSettings";

interface SnDateMonthBoxProps {
  defaultValue?: string;
  onValueChanged?: (value: string) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
}

const SnDateYearBox = React.memo(
  ({
    defaultValue,
    onValueChanged,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    inputRef,
    isSearchPanel = false,
    onFocus,
  }: SnDateMonthBoxProps) => {
    const [value, setValue] = useState<Dayjs | null>(
      defaultValue ? dayjs(defaultValue) : null
    );
    const [error, setError] = useState<boolean>(false);
    const [borderWidth, setBorderWidth] = useState("1px");
    const [backgroundColor, setBackgroundColor] = useState("#F1F1F1");

    const fetchDefaultValue = async () => {
      try {
        if (defaultValue) {
          setValue(dayjs(defaultValue));
        } else {
          setValue(null);
        }
      } catch (error: any) {}
    };
    const fetchIsRequired = async () => {
      try {
        if (isRequired) setBorderWidth("2px");
        else setBorderWidth("1px");
      } catch (error: any) {}
    };

    const fetchIsEditable = async () => {
      try {
        if (isEditable) setBackgroundColor("white");
        else setBackgroundColor("#F1F1F1");
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    useEffect(() => {
      fetchIsRequired();
    }, [isRequired]);

    useEffect(() => {
      fetchIsEditable();
    }, [isEditable]);

    const handleChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) setError(false);

      setValue(newValue);

      if (onValueChanged) {
        if (newValue) {
          const date = newValue.format("YYYY");
          if (date !== "Invalid Date") {
            onValueChanged(date);
          } else {
            onValueChanged("");
          }
        } else {
          onValueChanged("");
        }
      }
    };

    const handleError = (reason: any) => {
      // 일부만 입력된 경우를 에러로 판단
      if (value && reason === "invalidDate") {
        setError(true);
      } else {
        setError(false);
      }
    };

    const handleAccept = () => {
      setTimeout(() => {
        onFocus && onFocus("current");
      }, 10);
    };

    const handleKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        await (e.target as HTMLInputElement).blur();
        if (isSearchPanel) {
          e.preventDefault();
          onFocus && onFocus("current");
        } else {
          e.preventDefault();
          onFocus && onFocus("next");
        }
      } else if (e.key === "Tab") {
        await (e.target as HTMLInputElement).blur();
        e.preventDefault();
        onFocus && onFocus("next");
      }
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
        <DatePicker
          inputRef={inputRef}
          format="YYYY"
          views={["year"]}
          value={value ? dayjs(value) : null}
          onChange={handleChange}
          onAccept={handleAccept}
          onError={handleError}
          disabled={isReadOnly}
          sx={{
            width: "100%",
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
              fontSize: StyleSettings.font,
            },
            "& .MuiSvgIcon-root": {
              fontSize: StyleSettings.font,
            },
            backgroundColor: backgroundColor,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderWidth: borderWidth, // 테두리 두께 설정
              },
              "&:hover fieldset": {
                borderWidth: borderWidth, // 호버 시 두께 유지
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth, // 포커스 시 두께 유지
              },
            },
          }}
          slotProps={{
            textField: {
              placeholder: "",
              error: error,
              helperText: "",
              onKeyDown: handleKeyDown,
            },
            popper: {
              sx: {
                "& .MuiDateCalendar-root": {
                  height: "auto",
                },
                "& .MuiMonthCalendar-root": {
                  height: "auto",
                },
              },
            },
            openPickerButton: {
              tabIndex: -1, // Tab 키로 포커스 안 가게 설정
            },
          }}
        />
      </LocalizationProvider>
    );
  }
);

export default SnDateYearBox;
